/**
 * Feature-local navigation descriptors for the GvG planner surface.
 *
 * `gvgPlannerNavLinks(ctx)` returns the planner-owned navigation entries
 * (Calendar + Raid plan + Guild settings + Audit log + Onboarding). Each entry
 * carries its own `visible: ComputedRef<boolean>` driven by the planner's
 * own state shape and the host-level session / guild ports, mirroring the
 * visibility rules that used to live inline in `AppNav.vue`.
 *
 * Labels are stored as `ComputedRef<string>` so they react immediately
 * to locale flips from the host locale port — freezing the English
 * string at install time would leave the nav stuck on English forever.
 * The descriptor record itself is stable for the app's lifetime; only
 * the resolved label text follows the effective locale.
 *
 * The MyBuilds surface is owned by this feature too, but it lives in the
 * header popover (the profile chip → "Manage my builds" link in
 * `ProfilePopover.vue`), NOT in the primary nav strip — so it is exposed
 * via the profile-popover channel on `NavLinkDescriptor.profilePopover`
 * (see the `myBuilds` entry below).
 */
import { computed, type ComputedRef } from 'vue'
import type { Router } from 'vue-router'

import { IconName } from '@/shared/ui/BaseIcon.vue'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { useGuild } from '@/shared/ports/guild'
import { useSession } from '@/shared/ports/session'
import { useGvgPlannerStore } from './stores/gvgPlannerStore'
import { APP_ROUTE_NAMES } from '@/shared/constants/routes'
import { FEATURE_ROUTE_NAMES } from './constants/routes'
import { appHostEn } from '@/app/locales/en.ts'
import { appHostRu } from '@/app/locales/ru.ts'
import type { NavLinkDescriptor } from '@/app/nav'

export interface GvgPlannerNavCtx {
  /** The host-level router; currently unused but kept so future descriptors
   * (e.g. "external" links or programmatic deep-link surfaces) can take it. */
  readonly router: Router
}

const navCatalog = defineLocaleCatalog({ en: appHostEn, ru: appHostRu })
const navMessages = useLocaleMessages(navCatalog)

/**
 * Build the planner-owned nav-link descriptor list. Called once at
 * install-time; the returned descriptors' `visible` and `label` computeds
 * stay live for the rest of the app's lifetime.
 */
export function gvgPlannerNavLinks(
  _ctx: GvgPlannerNavCtx,
): ReadonlyArray<NavLinkDescriptor> {
  const store = useGvgPlannerStore()
  const session = useSession()
  const guild = useGuild()

  /** Calendar — always visible, mandatory landing surface. */
  const calendar: NavLinkDescriptor = {
    routeName: APP_ROUTE_NAMES.Calendar,
    label: computed<string>(() => navMessages.value.nav.calendar),
    icon: IconName.Calendar,
    visible: computed<boolean>(() => true),
    order: 10,
    params: computed<Readonly<Record<string, string>>>(() => ({
      guildId: guild.activeDiscordGuildId.value,
    })),
  }

  /** Raid plan — only when a valid raid has been selected and loaded.
   * The route requires `:guildId` and `:raidId` params; expose them via the reactive
   * `params` computed so the host `RouterLink` re-resolves the destination
   * every time the selected raid id changes. */
  const raidIdPattern = /^[1-9]\d*$/
  const activeRaidId: ComputedRef<string | null> = computed(() => {
    const id = store.activeRaidId
    const loadedRaidId = store.activeRaidBoard?.raid.id
    return typeof id === 'string' && loadedRaidId === id && raidIdPattern.test(id)
      ? id
      : null
  })
  const raidPlan: NavLinkDescriptor = {
    routeName: FEATURE_ROUTE_NAMES.GvgPlanner,
    label: computed<string>(() => navMessages.value.nav.raidPlan),
    icon: IconName.Shield,
    visible: computed<boolean>(() => activeRaidId.value !== null),
    order: 20,
    params: computed<Readonly<Record<string, string>> | undefined>(() =>
      activeRaidId.value !== null
        ? { guildId: guild.activeDiscordGuildId.value, raidId: activeRaidId.value }
        : undefined,
    ),
  }

  /** Officers — owner / officer only. */
  const officers: NavLinkDescriptor = {
    routeName: FEATURE_ROUTE_NAMES.Officers,
    label: computed<string>(() => navMessages.value.nav.officers),
    icon: IconName.Shield,
    visible: computed<boolean>(() => store.isOwner || store.isOfficer),
    order: 30,
    params: computed<Readonly<Record<string, string>>>(() => ({
      guildId: guild.activeDiscordGuildId.value,
    })),
  }

  /** Guild audit history — owner / officer only. */
  const auditLog: NavLinkDescriptor = {
    routeName: FEATURE_ROUTE_NAMES.AuditLog,
    label: computed<string>(() => navMessages.value.nav.auditLog),
    icon: IconName.LayoutDetail,
    visible: computed<boolean>(() => store.isOwner || store.isOfficer),
    order: 40,
    params: computed<Readonly<Record<string, string>>>(() => ({
      guildId: guild.activeDiscordGuildId.value,
    })),
  }

  /** Onboarding — only when signed-in, guild list settled, and no
   *  available guilds (the "first-time" state). */
  const onboarding: NavLinkDescriptor = {
    routeName: FEATURE_ROUTE_NAMES.Onboarding,
    label: computed<string>(() => navMessages.value.nav.onboarding),
    icon: IconName.Calendar,
    visible: computed<boolean>(
      () =>
        session.user.value !== null
        && !guild.loading.value
        && guild.availableGuilds.value.length === 0,
    ),
    order: 50,
  }

  /** Manage my builds — profile-popover entry. Cross-server build
   *  management; lives in the header popover rather than the primary nav. */
  const myBuilds: NavLinkDescriptor = {
    routeName: FEATURE_ROUTE_NAMES.MyBuilds,
    label: computed<string>(() => navMessages.value.nav.myBuilds),
    icon: IconName.LayoutDetail,
    visible: computed<boolean>(() => true),
    order: 10,
    profilePopover: {
      label: computed<string>(() => navMessages.value.profilePopover.myBuilds),
      icon: IconName.LayoutDetail,
    },
  }

  return [calendar, raidPlan, officers, auditLog, onboarding, myBuilds]
}
